import * as React from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

export type LogoSize = "sm" | "md" | "lg";

export interface LogoProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Visual scale of the logo. Defaults to "md". */
  size?: LogoSize;
  /** Render only the square mini mark (the favicon), without the wordmark. */
  markOnly?: boolean;
}

const sizeMap: Record<
  LogoSize,
  { img: string; height: number; width: number; mark: string; markPx: number }
> = {
  sm: { img: "h-6 w-auto", height: 24, width: 96, mark: "h-6 w-6", markPx: 24 },
  md: { img: "h-8 w-auto", height: 32, width: 128, mark: "h-8 w-8", markPx: 32 },
  lg: { img: "h-12 w-auto", height: 48, width: 192, mark: "h-12 w-12", markPx: 48 },
};

/**
 * Notiia logo — the original image mark. Full wordmark from /logo.png, or the
 * square favicon mark (/android-chrome-192x192.png) when `markOnly` is set.
 */
export function Logo({
  size = "md",
  markOnly = false,
  className,
  ...props
}: LogoProps) {
  const s = sizeMap[size];

  return (
    <span
      className={cn("inline-flex items-center", className)}
      aria-label="notiia"
      {...props}
    >
      {markOnly ? (
        <>
          <Image
            src="/android-chrome-192x192.png"
            alt="Notiia"
            height={s.markPx}
            width={s.markPx}
            className={cn(s.mark, "dark:hidden")}
          />
          {/* Same mark with the navy bars recolored white for dark surfaces */}
          <Image
            src="/logo-mark-dark.png"
            alt="Notiia"
            height={s.markPx}
            width={s.markPx}
            className={cn(s.mark, "hidden dark:block")}
          />
        </>
      ) : (
        <Image
          src="/logo.png"
          alt="Notiia"
          height={s.height}
          width={s.width}
          className={cn(s.img, "dark:invert")}
          priority={size === "lg"}
        />
      )}
    </span>
  );
}

export default Logo;
